import { Divider, Grid, Paper, styled, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Control, UseFormRegister } from "react-hook-form";
import { Sex } from "@commr/admin/models/user/user";
import { ResidentForm } from "@commr/admin/models/user/resident/resident";
import ControlledDatePicker from "../../common/formFields/controlledDatePicker";
import ControlledInput from "../../common/formFields/controlledInput";
import ControlledSelect from "../../common/formFields/controlledSelect";
import PhotoAvatarInput from "../../common/formFields/photoAvatarInput";

interface Props {
  control: Control<ResidentForm, any>;
  profilePicture: string;
  setProfilePicture: React.Dispatch<React.SetStateAction<string>>;
  register: UseFormRegister<ResidentForm>;
}

const ResidentGeneralInfos: React.FC<React.PropsWithChildren<Props>> = ({
  control,
  profilePicture,
  setProfilePicture,
  register,
}) => {
  const { t } = useTranslation();

  const Item = styled(Paper)(({ theme }) => ({
    ...theme.typography.body2,
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
  }));

  const sexOptions = Object.values(Sex).map((sex) => ({
    value: sex,
    name: t(`common:sexList.${sex.toLocaleLowerCase()}`),
    isDisabled: false,
  }));

  return (
    <Item>
      <Grid item sx={{ paddingBottom: 2 }}>
        <Typography variant="h5">{t("userForm:generalInfo")}</Typography>
        <Divider variant="fullWidth" />
      </Grid>
      <Grid item container spacing={1}>
        <Grid item xs={6}>
          <Grid
            container
            direction="row"
            spacing={1}
            style={{
              justifyContent: "center",
              display: "flex",
              alignContent: "center",
            }}
          >
            <PhotoAvatarInput
              name="profilePicture"
              register={register}
              profilePicture={profilePicture}
              setProfilePicture={setProfilePicture}
            />
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container direction="row" spacing={1}>
            <Grid item xs={6}>
              <ControlledInput
                controllerProps={{
                  name: "lastName",
                  control,
                  rules: {
                    maxLength: {
                      value: 50,
                      message: `${t("forms:errorMessages.maxLength", {
                        value: 50,
                      })}`,
                    },
                    minLength: {
                      value: 3,
                      message: `${t("forms:errorMessages.minLength", {
                        value: 3,
                      })}`,
                    },
                    required: {
                      value: true,
                      message: `${t("forms:errorMessages.required")}`,
                    },
                  },
                }}
                label={t("common:lastName")}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledInput
                controllerProps={{
                  name: "firstName",
                  control,
                  rules: {
                    maxLength: {
                      value: 50,
                      message: `${t("forms:errorMessages.maxLength", {
                        value: 50,
                      })}`,
                    },
                    minLength: {
                      value: 3,
                      message: `${t("forms:errorMessages.minLength", {
                        value: 3,
                      })}`,
                    },
                    required: {
                      value: true,
                      message: `${t("forms:errorMessages.required")}`,
                    },
                  },
                }}
                label={t("common:firstName")}
              />
            </Grid>

            <Grid item xs={6}>
              <ControlledSelect
                controllerProps={{
                  name: "sex",
                  control,
                }}
                label={t("common:sex")}
                options={sexOptions}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledDatePicker
                controllerProps={{
                  name: "birthDate",
                  control,
                }}
                label={t("common:birthDate")}
              />
            </Grid>
            <Grid item xs={3}>
              <ControlledInput
                controllerProps={{
                  name: "address.civicNumber",
                  control,
                }}
                label={t("common:civicNumber")}
              />
            </Grid>
            <Grid item xs={9}>
              <ControlledInput
                controllerProps={{
                  name: "address.street",
                  control,
                }}
                label={t("common:street")}
              />
            </Grid>
            <Grid item xs={8}>
              <ControlledInput
                controllerProps={{
                  name: "address.city",
                  control,
                }}
                label={t("common:city")}
              />
            </Grid>
            <Grid item xs={4}>
              <ControlledInput
                controllerProps={{
                  name: "address.postalCode",
                  control,
                }}
                label={t("common:postalCode")}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Item>
  );
};

export default ResidentGeneralInfos;
